import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Form, Button, Modal } from "react-bootstrap";
import ImageCarousel from "../../components/ImageCarousel/ImageCarousel";
import ImageUploader from "../../components/ImageUploader/ImageUploader";
import ItemCard from "../../components/ItemCard/ItemCard";
import supabase from "../../supabase";
import "./PropertyDetail.css";
import { useNotification } from "../../context/NotificationContext";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [images, setImages] = useState([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState(0);
  const [name, setName] = useState("");
  const [superficie, setSuperficie] = useState("");
  const [ageLogementAnnees, setAgeLogementAnnees] = useState("");
  const [commentaireUser, setCommentaireUser] = useState("");
  const [departements, setDepartements] = useState([]);
  const [selectedDepartementId, setSelectedDepartementId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [initialImageIds, setInitialImageIds] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: propertyData } = await supabase
          .from("properties")
          .select("*, departements(nom, numero)")
          .eq("id", id)
          .single();

        const { data: departementData } = await supabase
          .from("departements")
          .select("*")
          .order("numero", { ascending: true });

        const { data: roomsData, error: roomsError } = await supabase.rpc(
          "get_rooms_with_details",
          { p_property_id: id }
        );
        if (roomsError) throw roomsError;

        const roomsWithAnalyses = (roomsData || []).map((room) => ({
          ...room,
          images: room.default_image_url
            ? [{ url: room.default_image_url, is_default: true }]
            : [],
          latestAnalysis: room.latest_analysis,
        }));

        const { data: imagesData } = await supabase
          .from("images")
          .select("*")
          .eq("property_id", id);

        setProperty(propertyData);
        setDepartements(departementData || []);
        setRooms(roomsWithAnalyses);
        const normalizedImages = (imagesData || []).map((img) => ({ ...img, isNew: false }));
        setImages(normalizedImages);
        setInitialImageIds(normalizedImages.map((img) => img.id));
        setName(propertyData?.name || "");
        setSuperficie(propertyData?.superficie || "");
        setAgeLogementAnnees(propertyData?.age_logement_annees || "");
        setCommentaireUser(propertyData?.commentaire_user || "");
        setSelectedDepartementId(propertyData?.departement_id || null);

        const defaultIndex = (imagesData || []).findIndex((img) => img.is_default);
        setDefaultImageIndex(defaultIndex >= 0 ? defaultIndex : 0);
      } catch (error) {
        console.error("Erreur lors du chargement des données :", error);
        showNotification("Impossible de charger le bien.", "danger");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, showNotification]);

  const handleDeleteRoom = (roomId) => {
    setRoomToDelete(roomId);
    setShowDeleteModal(true);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;
    try {
      await supabase.from("images").delete().eq("room_id", roomToDelete);
      await supabase.from("rooms").delete().eq("id", roomToDelete);
      setRooms((prev) => prev.filter((r) => r.id !== roomToDelete));
      showNotification("Pièce supprimée avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression de la pièce :", error);
      showNotification(
        "Erreur lors de la suppression de la pièce. Veuillez réessayer.",
        "danger"
      );
    } finally {
      setShowDeleteModal(false);
      setRoomToDelete(null);
    }
  };

  const saveImagesAndRefresh = async () => {
    // Delete removed images (present initially, missing now)
    const currentIds = images.filter((img) => !img.isNew && img.id).map((img) => img.id);
    const idsToDelete = initialImageIds.filter((id) => !currentIds.includes(id));

    if (idsToDelete.length > 0) {
      await supabase.from("images").delete().in("id", idsToDelete);
    }

    // Insert new images and update default flags for existing ones
    const imagePromises = images.map((image, index) => {
      if (image.isNew) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const imageUrl = event.target.result;
            const { error } = await supabase.from("images").insert([
              {
                url: imageUrl,
                is_default: index === defaultImageIndex,
                property_id: id,
              },
            ]);
            if (error) reject(error);
            else resolve();
          };
          reader.readAsDataURL(image.file);
        });
      }
      return supabase
        .from("images")
        .update({ is_default: index === defaultImageIndex })
        .eq("id", image.id);
    });

    await Promise.all(imagePromises);

    // Refresh images from DB to sync IDs and default index
    const { data: refreshed } = await supabase
      .from("images")
      .select("*")
      .eq("property_id", id);
    const normalized = (refreshed || []).map((img) => ({ ...img, isNew: false }));
    setImages(normalized);
    setInitialImageIds(normalized.map((img) => img.id));
    const defIdx = normalized.findIndex((img) => img.is_default);
    setDefaultImageIndex(defIdx >= 0 ? defIdx : 0);
  };

  const handleSaveImages = async () => {
    try {
      await saveImagesAndRefresh();
      showNotification("Photos mises à jour.", "success");
      setShowPhotosModal(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des photos :", error);
      showNotification("Erreur lors de la mise à jour des photos.", "danger");
    }
  };

  const handleUpdateProperty = async () => {
    try {
      const { error: propertyError } = await supabase
        .from("properties")
        .update({
          name,
          superficie: parseFloat(superficie),
          age_logement_annees: parseInt(ageLogementAnnees),
          commentaire_user: commentaireUser,
          departement_id: selectedDepartementId,
        })
        .eq("id", id);
      if (propertyError) throw propertyError;

      await saveImagesAndRefresh();

      showNotification("Propriété mise à jour avec succès !", "success");
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
      showNotification("Erreur lors de la mise à jour.", "danger");
    }
  };

  const handleBack = () => navigate(-1);

  const getNoteFromAnalysis = (room) => {
    try {
      return (
        room?.latestAnalysis?.analysis_result?.note_etat_piece ?? "Non noté"
      );
    } catch (error) {
      return "Non noté";
    }
  };

  const getDepartementLabel = (depId) => {
    const dept = departements.find((dep) => dep.id === depId);
    return dept ? `${dept.numero} - ${dept.nom}` : "Non défini";
  };

  if (loading) return <p className="text-center">Chargement...</p>;
  if (!property) return <p className="text-center text-danger">Propriété introuvable</p>;

  return (
    <div className="container mt-4 property-detail-container">
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteRoom}
        title="Confirmer la suppression"
        body="Êtes-vous sûr de vouloir supprimer cette pièce et toutes ses analyses ? Cette action est irréversible."
      />

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <button
          className="btn btn-link text-decoration-none p-0 back-link"
          onClick={handleBack}
        >
          <i className="bi bi-arrow-left me-2" aria-hidden="true"></i>
          Retour
        </button>
        {/* Intentionally no actions here to avoid duplication with in-card buttons */}
      </div>

      {/* Title and meta */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="mb-0">{property.name}</h2>
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-light text-dark border">
            <i className="bi bi-geo-alt me-1" aria-hidden="true"></i>
            {getDepartementLabel(property.departement_id)}
          </span>
          <span className="badge bg-light text-dark border">
            <i className="bi bi-aspect-ratio me-1" aria-hidden="true"></i>
            {property.superficie} m²
          </span>
          <span className="badge bg-light text-dark border">
            <i className="bi bi-image me-1" aria-hidden="true"></i>
            {images?.length || 0} images
          </span>
          <span className="badge bg-light text-dark border">
            <i className="bi bi-door-open me-1" aria-hidden="true"></i>
            {rooms?.length || 0} pièces
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="row g-4 align-items-stretch">
        <div className="col-12 col-lg-8 d-flex">
          <Card className="property-info-card h-100 w-100 d-flex flex-column">
            <Card.Body className="d-flex flex-column">
              <ImageCarousel images={images} />
            </Card.Body>
          </Card>
        </div>
        <div className="col-12 col-lg-4 d-flex">
          {!isEditing ? (
            <Card className="property-info-card h-100 w-100 d-flex flex-column">
              <Card.Body className="d-flex flex-column justify-content-between">
                <div className="mb-3">
                  <div className="text-muted small mb-1">Age du logement</div>
                  <div className="fw-semibold">{property.age_logement_annees + " ans" ?? "—" } </div>
                </div>
                <div className="mb-3">
                  <div className="text-muted small mb-1">Commentaire</div>
                  <div className="fw-normal">{property.commentaire_user || "Aucun"}</div>
                </div>
                <div className="d-grid gap-2 mt-auto">
                  <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
                    <i className="bi bi-pencil me-1" aria-hidden="true"></i>
                    Modifier les informations
                  </button>
                  <button className="btn btn-outline-secondary" onClick={() => setShowPhotosModal(true)}>
                    <i className="bi bi-images me-1" aria-hidden="true"></i>
                    Gérer les photos
                  </button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="property-info-card h-100 w-100 d-flex flex-column">
              <Card.Body className="d-flex flex-column">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom du bien</Form.Label>
                    <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Superficie (m²)</Form.Label>
                    <Form.Control type="number" value={superficie} onChange={(e) => setSuperficie(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Âge du logement (années)</Form.Label>
                    <Form.Control type="number" value={ageLogementAnnees} onChange={(e) => setAgeLogementAnnees(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Département</Form.Label>
                    <Form.Select value={selectedDepartementId || ""} onChange={(e) => setSelectedDepartementId(Number(e.target.value))}>
                      <option value="">Sélectionner un département</option>
                      {departements.map((dep) => (
                        <option key={dep.id} value={dep.id}>
                          {dep.numero} - {dep.nom}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Commentaire utilisateur</Form.Label>
                    <Form.Control as="textarea" rows="4" value={commentaireUser} onChange={(e) => setCommentaireUser(e.target.value)} placeholder="Ajoutez un commentaire ici..." />
                  </Form.Group>
                  <div className="d-grid gap-2 mt-auto">
                    <Button className="btn btn-primary" onClick={handleUpdateProperty}>
                      Sauvegarder
                    </Button>
                    <Button className="btn btn-secondary" onClick={() => setIsEditing(false)}>
                      Annuler
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setShowPhotosModal(true)}>
                      <i className="bi bi-images me-1" aria-hidden="true"></i>
                      Gérer les photos
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* Photos modal */}
      <Modal show={showPhotosModal} onHide={() => setShowPhotosModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Gérer les photos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageUploader
            images={images}
            setImages={setImages}
            defaultImageIndex={defaultImageIndex}
            setDefaultImageIndex={setDefaultImageIndex}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPhotosModal(false)}>Fermer</Button>
          <Button className="btn btn-primary" onClick={handleSaveImages}>Enregistrer</Button>
        </Modal.Footer>
      </Modal>

      {/* Image uploader when editing (full width) */}
      {isEditing && (
        <Card className="property-info-card mt-4">
          <Card.Body>
            <h5 className="mb-3">Ajouter ou modifier des images</h5>
            <ImageUploader
              images={images}
              setImages={setImages}
              defaultImageIndex={defaultImageIndex}
              setDefaultImageIndex={setDefaultImageIndex}
            />
          </Card.Body>
        </Card>
      )}

      {/* Rooms */}
      <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
        <h4 className="mb-0">Pièces</h4>
        <a href={`/property/${id}/add-room`} className="btn btn-outline-primary btn-sm">
          <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>
          Ajouter une pièce
        </a>
      </div>
      <div className="row gx-4 gy-4">
        {rooms.map((room) => (
          <div key={room.id} className="col-12 col-sm-6 col-lg-4">
            <ItemCard
              title={room.name}
              text={`Superficie: ${room.superficie} m\u00B2`}
              note={`Note: ${getNoteFromAnalysis(room)}`}
              link={`/property/${id}/room/${room.id}`}
              linkText="Voir détails"
              images={room.images.map((img) => img.url)}
              onDelete={() => handleDeleteRoom(room.id)}
            />
          </div>
        ))}
        <div className="col-12 col-sm-6 col-lg-4">
          <ItemCard
            title="Ajouter une pièce"
            text="Cliquez pour ajouter une nouvelle pièce"
            images={[]}
            link={`/property/${id}/add-room`}
            linkText="Ajouter"
          />
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
